'use client';

import { PropsWithChildren, useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { DEFAULT_ACCESS_TOKEN_ROUTE, DEFAULT_LOGIN_ROUTE, DEFAULT_LOGOUT_ROUTE } from '../../shared/constants';
import { User } from '../../shared/models/user.model';

import { AuthenticationContextProvider } from './authentication-context-provider';
import { NoopAuthenticationContextProvider } from './noop-authentication-context-provider';
import { TokenProvider } from './token-provider';
import { UserProvider } from './user-provider';

function extractPathSegment(url: string): string {
   try {
      return new URL(url).pathname.replace(/\/$/, '');
   } catch {
      return '';
   }
}

type AuthenticationProvidersProps = PropsWithChildren & {
   isPublic: boolean;
   defaultToken?: string | undefined;
   initialUser?: User;
   nextJsAppFullUrl?: string;
   canAuthenticate?: boolean;
};

export const AuthenticationProviders = ({
   isPublic,
   initialUser,
   defaultToken,
   nextJsAppFullUrl,
   canAuthenticate,
   children
}: AuthenticationProvidersProps) => {
   const [queryClient] = useState(() => new QueryClient());

   if (isPublic) {
      return <NoopAuthenticationContextProvider>{children}</NoopAuthenticationContextProvider>;
   }

   const pathSegment = nextJsAppFullUrl ? extractPathSegment(nextJsAppFullUrl) : '';
   const profileUrl = `${pathSegment}/auth/profile`;
   const loginUrl = `${pathSegment}${DEFAULT_LOGIN_ROUTE}`;
   const logoutUrl = `${pathSegment}${DEFAULT_LOGOUT_ROUTE}`;
   const tokenUrl = `${pathSegment}${DEFAULT_ACCESS_TOKEN_ROUTE}`;

   return (
      <QueryClientProvider client={queryClient}>
         <UserProvider profileUrl={profileUrl} user={initialUser}>
            <TokenProvider defaultToken={defaultToken} tokenUrl={tokenUrl}>
               <AuthenticationContextProvider loginUrl={loginUrl} logoutUrl={logoutUrl} canAuthenticate={canAuthenticate}>
                  {children}
               </AuthenticationContextProvider>
            </TokenProvider>
         </UserProvider>
      </QueryClientProvider>
   );
};
